import axios from 'axios';

const API_BASE_URL = 'https://api.halisy.com/api';

// Create axios instance with proper CSRF configuration
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  withXSRFToken: true,
  timeout: 20000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    'Cache-Control': 'no-cache', 
    'Pragma': 'no-cache', 
  },
});

// Request interceptor to add auth token
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
axiosInstance.interceptors.response.use(
  (response) => {
    return response.data; // Return response.data to match your landingPageService expectations
  },
  (error) => {
    // Handle different error types
    if (error.response) {
      // Server responded with error status
      const { data, status } = error.response;
      let errorMessage = `HTTP ${status}: ${error.response.statusText}`;
      
      if (data) {
        errorMessage = data.message || data.error || errorMessage;
        
        // Handle validation errors
        if (data.errors) {
          const validationErrors = Object.values(data.errors).flat();
          errorMessage = validationErrors.join(', ');
        }
      }
      
      error.message = errorMessage;
    } else if (error.request) {
      // Network error
      error.message = 'Network error. Please check your connection.';
    }
    
    console.error('API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      error: error.message,
      status: error.response?.status,
    });
    
    return Promise.reject(error);
  }
);

// Helper function to ensure CSRF cookie is set
const initCsrfProtection = async () => {
  try {
    // Use the same axios instance for consistency
    await axiosInstance.get('https://api.halisy.com/sanctum/csrf-cookie');
    
  } catch (error) {
    console.warn('Failed to initialize CSRF protection:');
    // throw new Error('Failed to initialize CSRF protection');
  }
};

// Enhanced API call function - now includes CSRF for newsletter and contact
export const apiCall = async (endpoint, options = {}) => {
  try {
    // For POST requests that need CSRF protection
    const needsCSRF = options.method === 'POST' || 
                      endpoint === '/newsletter/subscribe' || 
                      endpoint === '/contact' ||
                      endpoint === '/login' || 
                      endpoint === '/register';
                      
    if (needsCSRF) {
      await initCsrfProtection();
      // Small delay to ensure cookie is set
      await new Promise(resolve => setTimeout(resolve, 50));
    }
    
    const response = await axiosInstance({
      url: endpoint,
      ...options,
    });
    
    return response; // axiosInstance already returns response.data via interceptor
  } catch (error) {
    throw error;
  }
};

export const apiConfig = {
  baseURL: API_BASE_URL,
  timeout: 20000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
};

export const endpoints = {
  // Landing Page Content
  landingPage: '/landing-page',
  contentByType: (type) => `/content/${type}`,
  
  // Newsletter
  newsletterSubscribe: '/newsletter/subscribe',
  
  // Contact
  contact: '/contact',
  
  // Auth
  register: '/register',
  login: '/login',
  logout: '/logout',
  user: '/user',
  
  // Product endpoints
  products: '/products',
  product: (slug) => `/products/${slug}`,
  productsFeatured: '/products/featured',
  productsCategories: '/products/categories',
  
  // Blog
  blog: '/blog',
  blogFeatured: '/blog/featured',
  blogAuthors: '/blog/authors',
  blogPost: (slug) => `/blog/${slug}`,
  
  // Cart
  cart: '/cart',
  cartAdd: '/cart',
  cartUpdate: (id) => `/cart/${id}`,
  cartRemove: (id) => `/cart/${id}`,
  cartClear: '/cart',
  
  // Order endpoints
  orders: '/orders',
  order: (id) => `/orders/${id}`,
  
  // Payment endpoints
  payment: '/payment/initialize',
  paymentVerify: (reference) => `/payment/verify/${reference}`,

   // Admin endpoints
  admin: {
    // Dashboard
    dashboard: '/admin/dashboard/stats',
    
    // Products
    products: '/admin/products',
    product: (id) => `/admin/products/${id}`,
    productToggleFeatured: (id) => `/admin/products/${id}/toggle-featured`,
    productBulkAction: '/admin/products/bulk-action',
    productCategories: '/admin/products/categories/list',
    productAccessories: '/admin/products/accessories/list',
    productCategoryOptions: (categoryId) => `/admin/products/category/${categoryId}/options`,
    
    // Accessories
    accessories: '/admin/accessories',
    accessory: (id) => `/admin/accessories/${id}`,
    
    // Categories
    categories: '/admin/categories',
    category: (id) => `/admin/categories/${id}`,
  }
};

export default axiosInstance;
export { API_BASE_URL };